import { DateAdapter } from './../../shared/adapters/date-adapter';
import { Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { type Address, NominatimLocation, ViaCEPResponse } from '../../shared/models/address.model';
import { environment as env } from './../../../environments/environment';
import { withDateAdapter, withDateAdapterPage } from '../../shared/rxjs/with-date-adapter';
import { type Pagination } from '../../shared/models/pagination.model';
import { type Coordinate } from '../../shared/models/geolocation.model';

@Injectable({
  providedIn: 'root'
})
export class AddressService {
  private readonly endpoint = `${env.baseAPIUrl}/address`;
  private readonly viaCEPURL = 'https://viacep.com.br/ws/';
  private readonly nominatimURL = 'https://nominatim.openstreetmap.org/reverse';

  private readonly dateFields = ['createdAt', 'updatedAt', 'deletedAt'];
  private readonly transformDate = withDateAdapter<Address>(this.dateFields);
  private readonly transformDatePage = withDateAdapterPage<Pagination<Address>>(this.dateFields);

  constructor(private readonly http: HttpClient) { }

  getAddressById(addressId: string): Observable<Address> {
    return this.http.get<Address>(`${this.endpoint}/${addressId}`)
      .pipe(this.transformDate);
  }

  paginateAddressByUserId(userId: string, page: number = 0, pageSize: number = 1): Observable<Pagination<Address>> {
    return this.http.get<Pagination<Address>>(`${this.endpoint}/user/${userId}?page=${page}&pageSize=${pageSize}`)
      .pipe(this.transformDatePage);
  }

  createAddress(address: Address): Observable<Address> {
    return this.http.post<Address>(`${this.endpoint}`, DateAdapter.toAPI(address, this.dateFields))
      .pipe(this.transformDate);
  }

  updateAddress(address: Address): Observable<Address> {
    return this.http.patch<Address>(`${this.endpoint}/${address.id}`, DateAdapter.toAPI(address, this.dateFields))
      .pipe(this.transformDate);
  }

  deleteAddress(addressId: string): Observable<void> {
    return this.http.delete<void>(`${this.endpoint}/${addressId}`)
  }

  fetchAddressByZipCode(zipCode: string): Observable<ViaCEPResponse> {
    return this.http.get<ViaCEPResponse>(`${this.viaCEPURL}${zipCode}/json`);
  }

  fetchAddressByCoordinates(coords: Coordinate, format: string = 'json', zoom: number = 17): Observable<NominatimLocation> {
    return this.http.get<NominatimLocation>(`${this.nominatimURL}?format=${format}&zoom=${zoom}&lat=${coords.latitude}&lon=${coords.longitude}`);
  }
}
